<template>
  <div
    :style="{ gap: `${tagGap}px` }"
    :class="[bem.b(), bem.is(size), bem.is(disabled ? 'disabled' : '')]"
  >
    <template v-for="(tag, idx) in newTags" :key="tag">
      <a-checkable-tag
        :style="tagMaxWidth ? { maxWidth: `${tagMaxWidth}px` } : {}"
        :class="[
          bem.e('tag-content'),
          bem.is(newValue.indexOf(tag) > -1 ? 'checked' : ''),
        ]"
        :checked="newValue.indexOf(tag) > -1"
        @change="(checked) => handleCheck(tag, checked)"
      >
        <div :class="bem.e('tag')">
          <span>{{ tag }}</span>
          <ll-icon
            v-if="showCloseIcon(idx)"
            icon-name="icon-close2"
            @click="handleClose(tag)"
          />
        </div>
      </a-checkable-tag>
    </template>
    <ll-tag v-if="!enableAdd && newTags.length === 0" shape="round">
      {{ emptyText || '暂无数据' }}
    </ll-tag>
    <!-- 新增标签 -->
    <template v-if="enableAdd">
      <ll-tag
        v-if="!inputVisible"
        shape="round"
        :icon="{
          iconName: 'icon-add',
        }"
        :class="[bem.e('add-tag'), bem.is(isDisabledAddTag ? 'disabled' : '')]"
        @click="showInput"
      >
        {{ addTagText }}
      </ll-tag>
      <ll-input
        v-else
        ref="inputRef"
        v-model:value.trim="inputValue"
        :placeholder="placeholder"
        size="small"
        :style="{ width: `${inputWidth}px` }"
        :class="[bem.e('input')]"
        @blur="handleInputConfirm"
        @keyup.enter="handleInputConfirm"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { checkableTagProps } from './checkable-tag'

import { createNamespace } from '@ll-plus/utils'

defineOptions({ name: 'LlCheckableTag' })

const props = defineProps(checkableTagProps)
const emits = defineEmits(['update:value', 'change'])

const bem = createNamespace('checkable-tag')

const inputRef = ref()
const inputValue = ref('')
const inputVisible = ref(false)
const newTags = ref([] as string[])

const newValue = computed({
  get() {
    return Array.isArray(props.value) ? props.value : []
  },
  set(val) {
    emits('update:value', val)
    emits('change', val)
  },
})

// 新增标签是否禁用
const isDisabledAddTag = computed(() => {
  return (
    props.disabled ||
    props.disabledAddTag ||
    (props.maxAddableTags && newTags.value.length >= props.maxAddableTags) ||
    (props.selectionMode === 'multiple' &&
      props.addAutoSelect &&
      props.maxChecked &&
      newValue.value.length >= props.maxChecked)
  )
})

const showCloseIcon = computed(() => {
  return (idx: number) => {
    if (props.deleteRule === 'all') {
      return true
    } else if (props.deleteRule === 'none') {
      return false
    } else if (props.deleteRule === 'exceptFirstN') {
      return idx >= props.exceptCount
    }
    return false
  }
})

const handleClose = (tag: string) => {
  if (props.disabled || props.disabledCloseTag) return

  newTags.value = newTags.value.filter((i) => i !== tag)
  if (newValue.value.includes(tag)) {
    newValue.value = newValue.value.filter((i) => i !== tag)
  }
}

// 选中/取消标签
const handleCheck = (tag: string, checked: boolean) => {
  if (props.disabled) return

  // 单选
  if (props.selectionMode === 'single') {
    if (!newValue.value.includes(tag)) {
      newValue.value = [tag]
    }
  }
  // 多选
  else {
    if (
      checked &&
      props.maxChecked &&
      newValue.value.length >= props.maxChecked
    ) {
      return
    }
    newValue.value = checked
      ? [...newValue.value, tag]
      : newValue.value.filter((i) => i !== tag)
  }
}

// 显示输入框
const showInput = () => {
  if (isDisabledAddTag.value) return

  inputVisible.value = true
  nextTick(() => {
    inputRef.value.focus()
  })
}

// 确认输入框
const handleInputConfirm = () => {
  if (inputValue.value && !newTags.value.includes(inputValue.value)) {
    newTags.value.push(inputValue.value)

    // 自动选择新增标签
    if (props.addAutoSelect) {
      if (props.selectionMode === 'single') {
        newValue.value = [inputValue.value]
      } else {
        newValue.value = [...newValue.value, inputValue.value]
      }
    }
  }

  inputVisible.value = false
  inputValue.value = ''
}

watch(
  () => props.selectionMode,
  (val) => {
    if (val === 'single') {
      newValue.value = [newValue.value[0]]
    }
  },
)

watch(
  () => props.options,
  (val) => {
    newTags.value = val
  },
  { immediate: true, deep: true },
)

watch(
  () => props.value,
  (val) => {
    if (!val || !Array.isArray(val)) return

    const newTagsSet = new Set(newTags.value)
    const addedTags = [] as string[]

    // 遍历 val 中的每个标签
    val.forEach((tag) => {
      if (tag && !newTagsSet.has(tag)) {
        // 这个标签不在 newTags 中，需要添加
        addedTags.push(tag)
        newTagsSet.add(tag) // 添加到 Set 避免重复
      }
    })

    // 如果有新标签需要添加
    if (addedTags.length > 0) {
      newTags.value = [...newTags.value, ...addedTags]
    }
  },
  { immediate: true, deep: true },
)
</script>
